import { CovCardOverlay } from "@cx-component-library/react-components";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { TrendSection } from "../trendData";
import "./CategoryCarousel.css";

const MAX_VISIBLE_CARDS = 6;

interface CategoryCarouselProps {
  sections: TrendSection[];
  /** Index of the currently active section panel, controlled externally. */
  activeIndex?: number;
  /** Callback fired when the user selects a panel. */
  onCategorySelect: (index: number) => void;
}

export const CategoryCarousel = ({
  sections,
  activeIndex = 0,
  onCategorySelect,
}: CategoryCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setCanScroll(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sections.length]);

  useEffect(() => {
    requestAnimationFrame(() => {
      for (let i = 0; i < panelRefs.current.length; i++) {
        const card = panelRefs.current[i]?.querySelector(
          ".cov-card-overlay--cinematic",
        ) as HTMLElement | null;
        if (!card) continue;
        const event = i === activeIndex ? "mouseover" : "mouseout";
        card.dispatchEvent(
          new MouseEvent(event, {
            bubbles: true,
            relatedTarget: document.body,
          }),
        );
      }
    });
  }, [activeIndex]);

  const scrollBy = (dir: 1 | -1) => {
    scrollRef.current?.scrollBy({
      left: dir * (Math.min(scrollRef.current.clientWidth * 0.4, 520) + 3),
      behavior: "smooth",
    });
  };

  const handlePanelClick = (index: number) => {
    onCategorySelect(index);
  };

  const indexTotal = String(sections.length).padStart(2, "0");

  return (
    <div className={`cat-carousel${canScroll ? "" : " cat-carousel--fit"}`}>
      <div ref={scrollRef} className="cat-carousel__scroll">
        {sections.map((section, i) => (
          <CategoryPanel
            key={section.title}
            ref={(el) => {
              panelRefs.current[i] = el;
            }}
            section={section}
            index={i}
            indexTotal={indexTotal}
            isActive={i === activeIndex}
            onClick={() => handlePanelClick(i)}
          />
        ))}
        {canScroll && <div style={{ flex: "0 0 84px" }} aria-hidden="true" />}
      </div>

      {canScroll && (
        <>
          <NavArrow direction="prev" onClick={() => scrollBy(-1)} />
          <NavArrow direction="next" onClick={() => scrollBy(1)} />
        </>
      )}

      <div className="cat-carousel__accent" aria-hidden="true" />
    </div>
  );
};

/* ── Nav arrow ────────────────────────────────────────────────────────── */

const NavArrow = ({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`cat-carousel__arrow cat-carousel__arrow--${direction}`}
    aria-label={direction === "prev" ? "Previous category" : "Next category"}
    onClick={onClick}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <path
        d={direction === "prev" ? "M11 4L6 9l5 5" : "M7 4l5 5-5 5"}
        stroke="rgba(255,255,255,0.80)"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

/* ── Category panel ───────────────────────────────────────────────────── */

interface CategoryPanelProps {
  section: TrendSection;
  index: number;
  indexTotal: string;
  isActive: boolean;
  onClick: () => void;
}

const CategoryPanel = forwardRef<HTMLDivElement, CategoryPanelProps>(
  ({ section, index, indexTotal, isActive, onClick }, ref) => {
    const visibleCards = section.cards.slice(0, MAX_VISIBLE_CARDS);
    const extraCount = Math.max(0, section.cards.length - MAX_VISIBLE_CARDS);

    const hoverContent = (
      <div className="cat-carousel__panel-items-wrap">
        <div className="cov-card-overlay__cin-divider" aria-hidden="true" />
        <div className="cat-carousel__panel-items">
          {visibleCards.map((card) => (
            <div key={card.title} className="cat-carousel__panel-item">
              <div className="cat-carousel__panel-bullet" />
              <span className="cat-carousel__panel-item-label">
                {card.title}
              </span>
            </div>
          ))}
          {extraCount > 0 && (
            <span className="cat-carousel__panel-more">+{extraCount} more</span>
          )}
        </div>
      </div>
    );

    return (
      <div
        ref={ref}
        className={`cat-carousel__panel-wrapper${isActive ? " cat-carousel__panel-wrapper--active" : ""}`}
      >
        <span className="cat-carousel__panel-label">
          <span className="cat-carousel__panel-label-index">
            {String(index + 1).padStart(2, "0")} / {indexTotal}
          </span>
          Explore Categories
        </span>
        <CovCardOverlay
          variant="cinematic"
          image={section.heroImage ?? section.cards[0]?.image ?? ""}
          header={section.title}
          hoverContent={hoverContent}
          lightStreak
          onCardClick={onClick}
          style={{ height: "100%", width: "100%" }}
        />
      </div>
    );
  },
);
